import { diffChars, diffLines } from 'diff';
import type { DiffRowStatus, DiffSection, DiffStats } from './types';

export type TextLineType = 'ctx' | 'add' | 'del';

export interface WordSeg {
  s: string;
  hl?: boolean;
}

export interface DiffTextLine {
  t: TextLineType;
  a?: number;
  b?: number;
  w: WordSeg[];
}

const toLines = (value: string): string[] => {
  const lines = value.split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines;
};

// Without a trailing newline diffLines treats the last line as distinct from
// the same line followed by more text, producing a false del/add pair.
const normalize = (value: string): string =>
  value === '' || value.endsWith('\n') ? value : `${value}\n`;

const wordSegs = (
  oldLine: string | undefined,
  newLine: string | undefined,
  side: 'del' | 'add'
): WordSeg[] => {
  const own = side === 'del' ? oldLine : newLine;
  if (own === undefined) return [{ s: '' }];
  const other = side === 'del' ? newLine : oldLine;
  if (other === undefined) return [{ s: own }];
  const segs: WordSeg[] = [];
  for (const part of diffChars(oldLine ?? '', newLine ?? '')) {
    if (side === 'del' && part.added) continue;
    if (side === 'add' && part.removed) continue;
    segs.push(part.added || part.removed ? { s: part.value, hl: true } : { s: part.value });
  }
  return segs.length ? segs : [{ s: own }];
};

/**
 * Line diff of two raw texts with word-level highlight of paired del/add lines
 * (paired by position within each del/add run, like git's unified diff).
 */
export const computeTextLines = (oldText: string, newText: string): DiffTextLine[] => {
  const out: DiffTextLine[] = [];
  let a = 1;
  let b = 1;
  const chunks = diffLines(normalize(oldText ?? ''), normalize(newText ?? ''));
  let i = 0;
  while (i < chunks.length) {
    const chunk = chunks[i];
    if (!chunk.added && !chunk.removed) {
      for (const line of toLines(chunk.value))
        out.push({ t: 'ctx', a: a++, b: b++, w: [{ s: line }] });
      i++;
      continue;
    }
    let delLines: string[] = [];
    let addLines: string[] = [];
    if (chunk.removed) {
      delLines = toLines(chunk.value);
      i++;
      if (chunks[i]?.added) {
        addLines = toLines(chunks[i].value);
        i++;
      }
    } else {
      addLines = toLines(chunk.value);
      i++;
    }
    for (let k = 0; k < delLines.length; k++)
      out.push({ t: 'del', a: a++, w: wordSegs(delLines[k], addLines[k], 'del') });
    for (let k = 0; k < addLines.length; k++)
      out.push({ t: 'add', b: b++, w: wordSegs(delLines[k], addLines[k], 'add') });
  }
  return out;
};

export type SplitPair = [DiffTextLine | null, DiffTextLine | null];

/** Pairs del/add runs side by side for the split view. */
export const splitPairs = (lines: DiffTextLine[]): SplitPair[] => {
  const pairs: SplitPair[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.t === 'ctx') {
      pairs.push([line, line]);
      i++;
      continue;
    }
    const dels: DiffTextLine[] = [];
    const adds: DiffTextLine[] = [];
    while (i < lines.length && lines[i].t === 'del') dels.push(lines[i++]);
    while (i < lines.length && lines[i].t === 'add') adds.push(lines[i++]);
    const n = Math.max(dels.length, adds.length);
    for (let k = 0; k < n; k++) pairs.push([dels[k] ?? null, adds[k] ?? null]);
  }
  return pairs;
};

export const sectionStats = (sec: DiffSection, textLines?: DiffTextLine[]): DiffStats => {
  if (sec.kind === 'text') {
    const lines = textLines ?? computeTextLines(sec.old, sec.new);
    return {
      add: lines.filter((l) => l.t === 'add').length,
      del: lines.filter((l) => l.t === 'del').length,
      mod: 0,
    };
  }
  const rows: { status: DiffRowStatus }[] = sec.rows;
  return {
    add: rows.filter((r) => r.status === 'added').length,
    del: rows.filter((r) => r.status === 'removed').length,
    mod: rows.filter((r) => r.status === 'changed').length,
  };
};

export const diffTotals = (stats: DiffStats[]): DiffStats =>
  stats.reduce((acc, s) => ({ add: acc.add + s.add, del: acc.del + s.del, mod: acc.mod + s.mod }), {
    add: 0,
    del: 0,
    mod: 0,
  });
